import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { TemplateCategoryEntity } from '@entities/TemplateCategory/Types';
import { ApiResult } from '@shared/Interfaces/Api/ApiResult';
import { TemplatesGateway } from '../../Processes/Gateways/Templates/TemplatesGateway';
import { RawTemplate, TemplateEntity } from '@entities/Template/Types';
import { TemplateModel } from '@entities/Template/TemplateModel';
import { LOCAL_STORAGE_KEYS } from '@configs/LocalStorageKeys';
import { PurchaseTemplateResponse } from '../../Processes/Gateways/Templates/Interfaces/PurchaseTemplateResponse';
import { PurchaseTemplateArguments } from '../../Processes/Gateways/Templates/Interfaces/PurchaseTemplateArguments';
import { TEMPLATE_PRICE } from '@shared/Constants/TemplatePrice';
import { TemplateForPurchase } from '../../Processes/Gateways/Templates/Interfaces/TemplateForPurchase';
import { SESSION_STORAGE_KEYS } from '@configs/SessionStorageKeys';

const templatesGateway = new TemplatesGateway();
const templateModel = new TemplateModel();

interface State {
    templatesCategories: TemplateCategoryEntity[];
    currentTemplate: TemplateEntity | null;
    templateDescription: string | null;
    shouldRedirectToNewProjectAfterAuthorization: boolean;
    isTemplateDataLoading: boolean;

    fetchAllCategories: () => Promise<ApiResult<TemplateCategoryEntity[]>>;
    fetchTemplateHtmlContent: (templateId: number) => Promise<void>;
    fetchTemplateById: (templateId: number) => Promise<ApiResult<RawTemplate>>;
    fetchPurchaseTemplate: (
        idStore: string,
        email: string
    ) => Promise<ApiResult<PurchaseTemplateResponse>>;
    landingTemplatesInitialization: (
        templates: RawTemplate[]
    ) => TemplateEntity[];
    removeTemplateDescription: () => void;
    setShouldRedirectToNewProjectAfterAuthorization: (
        shouldRedirect: boolean
    ) => void;
}

export const useTemplatesViewModel = create<State>()(
    persist(
        (set, get) => ({
            templatesCategories: [],
            currentTemplate: null,
            templateDescription: null,
            landingTemplates: [],
            shouldRedirectToNewProjectAfterAuthorization: false,

            isTemplateDataLoading: false,

            fetchAllCategories: async () => {
                const response = await templatesGateway.getAllCategories();
                if (!response.error && response.data) {
                    set({ templatesCategories: response.data });
                }
                return response;
            },

            fetchTemplateHtmlContent: async (templateId: number) => {
                set({ isTemplateDataLoading: true });
                const response =
                    await templatesGateway.getTemplateById(templateId);

                if (response.error || !response.data) {
                    set({ isTemplateDataLoading: false });
                    return;
                }

                templateModel.initialize(response.data);
                set({ currentTemplate: templateModel });

                if (!response.data.description) {
                    set({
                        isTemplateDataLoading: false,
                        templateDescription: '',
                    });
                    return;
                }

                const descriptionResponse =
                    await templatesGateway.getTemplateDescription(
                        response.data.description
                    );

                if (descriptionResponse.error || !descriptionResponse.data) {
                    set({ isTemplateDataLoading: false });
                    return;
                }

                set({
                    templateDescription: descriptionResponse.data.description,
                    isTemplateDataLoading: false,
                });
            },

            fetchTemplateById: async (templateId: number) => {
                set({ isTemplateDataLoading: true });
                const response =
                    await templatesGateway.getTemplateById(templateId);
                if (!response.error && response.data) {
                    templateModel.initialize(response.data);
                }
                set({
                    isTemplateDataLoading: false,
                    currentTemplate: templateModel,
                });
                return response;
            },

            fetchPurchaseTemplate: async (idStore: string, email: string) => {
                const { currentTemplate } = get();
                const body: PurchaseTemplateArguments = {
                    action: 'getPayment',
                    idStore: idStore,
                    templates: [
                        {
                            id: currentTemplate?.id,
                            price: currentTemplate?.paid
                                ? Number(TEMPLATE_PRICE)
                                : 0,
                        } as TemplateForPurchase,
                    ],
                    email: email,
                    totalPrice: currentTemplate?.paid
                        ? Number(TEMPLATE_PRICE)
                        : 0,
                };
                return await templatesGateway.purchaseTemplate(body);
            },

            landingTemplatesInitialization: (templates: RawTemplate[]) => {
                return templates.map(template => {
                    const newTemplateModel = new TemplateModel();
                    newTemplateModel.initialize(template);
                    return newTemplateModel;
                });
            },

            removeTemplateDescription: () => {
                set({ templateDescription: null });
            },

            setShouldRedirectToNewProjectAfterAuthorization: (
                shouldRedirect: boolean
            ) => {
                set({
                    shouldRedirectToNewProjectAfterAuthorization:
                        shouldRedirect,
                });
            },
        }),

        {
            name: SESSION_STORAGE_KEYS.TEMPLATE_FOR_PURCHASE,
            storage: createJSONStorage(() => sessionStorage),
            partialize: state => ({
                currentTemplate: state.currentTemplate,
                shouldRedirectToNewProjectAfterAuthorization:
                    state.shouldRedirectToNewProjectAfterAuthorization,
            }),
        }
    )
);
